import type { ReactNode } from "react";
import {
  MediaDetailDetails,
  MediaDetailLayout,
  MediaDetailMedia,
} from "src/components/media-detail";
import { AdDrilldownPrimitive } from "src/pages/ad-studio/insights/ad-drilldown-primitive";
import { AdDrilldownSkeleton } from "src/pages/ad-studio/insights/ad-drilldown-skeleton";
import { FailureAlert } from "src/pages/ad-studio/insights/performance/failure-alert";
import type {
  ListedAd,
  DrillScope,
  DrillScopeResult,
} from "src/pages/ad-studio/insights/performance/types";
import { useAdDrilldown } from "src/pages/ad-studio/insights/use-ad-drilldown";

type AdDrilldownProps = {
  drill: DrillScopeResult;
  id: string;
  listed: ListedAd | "no-list-row";
  children?: ReactNode;
};

const LoadedAdDrilldown = ({
  drill,
  id,
  listed,
  children,
}: Omit<AdDrilldownProps, "drill"> & { drill: DrillScope }) => {
  const { state } = useAdDrilldown({ drill, adId: id, listed });
  return (
    <AdDrilldownPrimitive.Root
      // A new ad starts on its first creative.
      key={id}
      header={state.header}
      media={state.media}
      metrics={state.metrics}
      daily={state.daily}
      tags={state.tags}
      ad={state.ad}
      suggestedActions={state.suggestedActions}
    >
      <MediaDetailLayout>
        <MediaDetailMedia>
          <AdDrilldownPrimitive.Media />
        </MediaDetailMedia>
        <MediaDetailDetails>
          <AdDrilldownPrimitive.Header />
          {children}
          <AdDrilldownPrimitive.Metrics />
          <AdDrilldownPrimitive.Daily />
          <AdDrilldownPrimitive.Tags />
        </MediaDetailDetails>
      </MediaDetailLayout>
    </AdDrilldownPrimitive.Root>
  );
};

export const AdDrilldown = ({
  drill,
  id,
  listed,
  children,
}: AdDrilldownProps) => {
  switch (drill.status) {
    case "loading": {
      return <AdDrilldownSkeleton />;
    }
    case "failed": {
      return <FailureAlert failure={drill.failure} />;
    }
    case "loaded": {
      return (
        <LoadedAdDrilldown drill={drill.value} id={id} listed={listed}>
          {children}
        </LoadedAdDrilldown>
      );
    }
    default: {
      return drill satisfies never;
    }
  }
};
